import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap, tap } from 'rxjs';
import { ApiService } from './api.service';
import { SessionMember, SessionRole, PresenceState } from '@crowdctrl/core';

/**
 * Service for session member management API calls.
 * Manages session role assignments stored in Firestore subcollection:
 * /tenants/{tenantId}/workspaces/{workspaceId}/sessions/{sessionId}/members/{userId}
 */
@Injectable({
  providedIn: 'root',
})
export class SessionMemberService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';
  private myMemberCache = new Map<
    string,
    { expiresAt: number; payload: { member: SessionMember | null; joined: boolean } }
  >();
  private static readonly MY_MEMBER_CACHE_TTL_MS = 20_000;

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get all members for a session with optional pagination.
   */
  getSessionMembers(
    sessionId: string,
    workspaceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    members: SessionMember[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        members: SessionMember[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/session_members_api/api/sessions/${sessionId}/members`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a specific session member by member ID
   */
  getSessionMember(
    sessionId: string,
    memberId: string,
    workspaceId: string,
  ): Observable<{ member: SessionMember }> {
    return from(
      this.apiService.get<{ member: SessionMember }>(
        this.baseUrl,
        `/session_members_api/api/sessions/${sessionId}/members/${memberId}`,
        { workspaceId },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  getMySessionMember(
    sessionId: string,
    workspaceId: string,
    useCache = true,
  ): Observable<{ member: SessionMember | null; joined: boolean }> {
    const cacheKey = `${workspaceId}:${sessionId}`;
    const now = Date.now();
    const cached = this.myMemberCache.get(cacheKey);
    if (useCache && cached && cached.expiresAt > now) {
      return new Observable<{ member: SessionMember | null; joined: boolean }>((subscriber) => {
        subscriber.next(cached.payload);
        subscriber.complete();
      });
    }

    return from(
      this.apiService.get<{ member: SessionMember | null; joined: boolean }>(
        this.baseUrl,
        `/session_members_api/api/sessions/${sessionId}/members/me`,
        { workspaceId },
      ),
    ).pipe(
      switchMap((obs) => obs),
      tap((payload) => {
        this.myMemberCache.set(cacheKey, {
          expiresAt: Date.now() + SessionMemberService.MY_MEMBER_CACHE_TTL_MS,
          payload,
        });
      }),
    );
  }

  /**
   * Add a member to a session with a specific role
   */
  addSessionMember(
    sessionId: string,
    userId: string,
    role: SessionRole,
    workspaceId: string,
  ): Observable<{ member: SessionMember }> {
    return from(
      this.apiService.post<{ member: SessionMember }, { userId: string; role: SessionRole }>(
        this.baseUrl,
        `/session_members_api/api/sessions/${sessionId}/members?workspaceId=${encodeURIComponent(workspaceId)}`,
        {
          userId,
          role,
        },
      ),
    ).pipe(
      switchMap((obs) => obs),
      tap(() => this.invalidateMySessionMemberCache(sessionId, workspaceId)),
    );
  }

  /**
   * Update a session member's role by member ID
   */
  updateSessionMemberRole(
    sessionId: string,
    memberId: string,
    role: SessionRole,
    workspaceId: string,
  ): Observable<{ member: SessionMember }> {
    return from(
      this.apiService.put<{ member: SessionMember }, { role: SessionRole }>(
        this.baseUrl,
        `/session_members_api/api/sessions/${sessionId}/members/${memberId}?workspaceId=${encodeURIComponent(workspaceId)}`,
        {
          role,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update a session member's presence state by member ID
   */
  updateSessionMemberPresence(
    sessionId: string,
    memberId: string,
    presenceState: PresenceState,
    workspaceId: string,
  ): Observable<{ member: SessionMember }> {
    return from(
      this.apiService.put<{ member: SessionMember }, { presenceState: PresenceState }>(
        this.baseUrl,
        `/session_members_api/api/sessions/${sessionId}/members/${memberId}/presence?workspaceId=${encodeURIComponent(workspaceId)}`,
        {
          presenceState,
        },
      ),
    ).pipe(
      switchMap((obs) => obs),
      tap(() => this.invalidateMySessionMemberCache(sessionId, workspaceId)),
    );
  }

  /**
   * Remove a member from a session by member ID
   */
  removeSessionMember(sessionId: string, memberId: string, workspaceId: string): Observable<void> {
    return from(
      this.apiService.delete<void>(
        this.baseUrl,
        `/session_members_api/api/sessions/${sessionId}/members/${memberId}?workspaceId=${encodeURIComponent(workspaceId)}`,
      ),
    ).pipe(
      switchMap((obs) => obs),
      tap(() => this.invalidateMySessionMemberCache(sessionId, workspaceId)),
    );
  }

  invalidateMySessionMemberCache(sessionId?: string, workspaceId?: string): void {
    if (sessionId && workspaceId) {
      this.myMemberCache.delete(`${workspaceId}:${sessionId}`);
      return;
    }
    this.myMemberCache.clear();
  }

  /**
   * Get the current user's role in a session
   */
  getUserSessionRole(
    sessionId: string,
    userId: string,
    workspaceId: string,
  ): Observable<SessionRole | null> {
    return from(
      this.apiService.get<{ role: SessionRole | null }>(
        this.baseUrl,
        `/session_members_api/api/sessions/${sessionId}/members/${userId}/role`,
        { workspaceId },
      ),
    ).pipe(
      switchMap((obs) => obs),
      switchMap((response) => {
        return new Observable<SessionRole | null>((subscriber) => {
          subscriber.next(response.role);
          subscriber.complete();
        });
      }),
    );
  }

  /**
   * List sessions the current user is a member of (across workspaces)
   */
  listMySessions(
    status?: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: Array<{
      workspaceId: string;
      workspaceName: string;
      sessionId: string;
      sessionName: string;
      status?: string;
      created?: { at?: string; by?: { id?: string; email?: string } };
    }>;
    retrievedAt?: string;
    pagination?: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount?: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string> = {};
    if (status) {
      params['status'] = status;
    }
    if (limit) {
      params['limit'] = limit.toString();
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        items: Array<{
          workspaceId: string;
          workspaceName: string;
          sessionId: string;
          sessionName: string;
          status?: string;
          created?: { at?: string; by?: { id?: string; email?: string } };
        }>;
        retrievedAt?: string;
        pagination?: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount?: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/session_members_api/api/sessions/memberships`, params),
    ).pipe(switchMap((obs) => obs));
  }
}
